import settings from '@config/settings';
import { generalSaveError } from '@store/alerts/alertConstants';
import { setAlert } from '@store/alerts/alertsState';
import {
  selectCreatorFromCustomCurricula,
  selectGoalList,
  selectGoalsAndSectionAndGoalListFromCurriculum,
  selectLayeredAnnotationsObjFromUrlOrParams,
} from '@store/curriculum/curriculumSelectors';
import { selectCurrentSchoolHref } from '@store/userAndSchool/userAndSchoolSelectors';
import { getPositioningObject } from '@utils/annotationHelper';
import { CUSTOMCREATIONTYPES, CUSTOMTYPES, createCustomItem } from '@utils/curriculumHelper';
import { logAndCaptureException } from '@utils/logAndCaptureException';
import { getReadOrder } from '@utils/utils';
import { omit } from 'lodash-es';
import { eventChannel } from 'redux-saga';
import { call, delay, put, race, take, takeEvery } from 'redux-saga/effects';
import type { BatchRequest } from '../../types/sriTypes';
import { excludeItem, generateCustomItemBatch, moveItemHelper } from './llinkidApiHelper';
import {
  addCustomGoal,
  addCustomSection,
  changeItemTitle,
  completeDistributionOrDeleteItem,
  distributeGoal,
  editCustomGoal,
  includeItem,
  moveItem,
  referenceGoal,
  saveLlinkidApiBatch,
  setAutoSync,
} from './llinkidApiState';
import type {
  AddCustomGoalPayloadAction,
  AddCustomSectionPayloadAction,
  ReferenceGoalPayloadAction,
} from './llinkidApiTypes';
import {
  selectAddCustomSectionBatch,
  selectDistributeGoalBatch,
  selectDistributeGoalFromAnnotationsBatch,
} from './userActionsSelectors';
import { typedSelect } from '@utils/sagaUtils';

function* includeItemSaga(action) {
  const { item } = action.payload;
  const { layeredAnnotationsObj } = yield* typedSelect((state) =>
    selectLayeredAnnotationsObjFromUrlOrParams(state),
  );

  try {
    const batch = excludeItem(item, layeredAnnotationsObj) as BatchRequest;
    yield put(saveLlinkidApiBatch({ batch, applyInstantly: true }));
  } catch (e) {
    logAndCaptureException(e);
    yield put(setAlert(generalSaveError));
  }
}

function* completeDistributionOrDeleteItemSaga(action) {
  const { item } = action.payload;

  try {
    const batch: BatchRequest = yield* typedSelect((state) =>
      selectDistributeGoalFromAnnotationsBatch(state, {
        goalsPositions: [
          {
            goal: item,
            position: { readOrder: getReadOrder(item) },
          },
        ],
      }),
    );
    yield put(saveLlinkidApiBatch({ batch, applyInstantly: true }));
  } catch (e) {
    logAndCaptureException(e);
    yield put(setAlert(generalSaveError));
  }

  // yield put(saveLlinkidApiBatch({ batch, applyInstantly: true }));
}

function* distributeGoalSaga(action) {
  const { selectedCurr, goalHref } = action.payload;
  try {
    const batch = yield* typedSelect((state) =>
      selectDistributeGoalBatch(state, { resources: selectedCurr, goalHref }),
    );

    yield put(saveLlinkidApiBatch({ batch, applyInstantly: true }));
  } catch (e) {
    logAndCaptureException(e);
    yield put(setAlert(generalSaveError));
  }
}

function* moveItemSaga(action) {
  try {
    const { parent, sibling, item } = action.payload;
    const { layeredAnnotationsObj } = yield* typedSelect((state) =>
      selectLayeredAnnotationsObjFromUrlOrParams(state),
    );
    const { curriculumKey, setid, studids, custids } = yield* typedSelect(
      (state) => state.curriculum,
    );
    const list = yield* typedSelect((state) =>
      selectGoalsAndSectionAndGoalListFromCurriculum(state, {
        curriculumKey,
        setid,
        studids,
        custids,
      }),
    );

    if (!list) {
      throw new Error('No curriculum loaded');
    }

    let fullParent = list.find((e) => e.href === parent);
    const fullItem = list.find((e) => e.href === item);

    let siblings = {
      above: undefined,
      below: undefined,
      readOrder: 1,
    };

    if (fullParent?.$$meta.permalink === '/content/FOUNDATIONALPLACEHOLDER') {
      const goalList = yield* typedSelect(selectGoalList);
      fullParent = goalList;
    }

    if (sibling) {
      const siblingIndex = fullParent?.goals?.findIndex((g) => g.href === sibling);
      const above = fullParent?.goals?.[siblingIndex];
      const below = fullParent?.goals?.[siblingIndex + 1];

      siblings = {
        above,
        below,
        readOrder: 1,
      };
    } else if (fullParent?.goals?.length) {
      const [firstChild] = fullParent.goals;
      siblings.below = firstChild;
    }

    const batch = moveItemHelper({
      draggedItem: { href: fullItem.href, annotation: fullItem.annotation },
      parent: { href: fullParent.href, annotation: fullParent.annotation },
      siblings,
      layeredAnnotationsObj,
    }) as BatchRequest;

    yield put(saveLlinkidApiBatch({ batch, applyInstantly: true }));
  } catch (e) {
    logAndCaptureException(e);
    yield put(setAlert(generalSaveError));
  }
}

function* changeTitleItemSaga(action) {
  const { title, item } = action.payload;
  const { key } = item;

  const batchBody = {
    $$meta: item.$$meta,
    creator: item.creator,
    context: item.context,
    type: item.type,
    key: item.key,
    description: title,
  };

  const batch = [
    { verb: 'PUT', href: `/llinkid/customcurriculum/customitems/${key}`, body: batchBody },
  ] as BatchRequest;

  yield put(saveLlinkidApiBatch({ batch, applyInstantly: true }));
}

function* editCustomGoalSaga(action) {
  const goal = action.payload;
  const { key } = goal;

  const batchBody = {
    $$meta: goal.$$meta,
    creator: goal.creator,
    context: goal.context,
    type: goal.type,
    key: goal.key,
    identifier: goal.identifier,
    description: goal.description,
    mandatory: goal.mandatory,
  };

  const batch = [
    { verb: 'PUT', href: `/llinkid/customcurriculum/customitems/${key}`, body: batchBody },
  ] as BatchRequest;

  yield put(saveLlinkidApiBatch({ batch, applyInstantly: true }));
}

function* referenceGoalSaga(action: ReferenceGoalPayloadAction) {
  const { goals, parent } = action.payload;
  // /find the above and below in the VM, the only info we have is item.parent which is basically the originator, the goal or item where the modal was opened.
  // /calculate the positions for the list of item.goals.
  // /alter the position object to have a readOrder.

  const goalListItems = yield* typedSelect(selectGoalsAndSectionAndGoalListFromCurriculum);

  const positions = getPositioningObject(
    parent,
    goals,
    CUSTOMCREATIONTYPES.llinkidGoal,
    goalListItems,
  );

  const resultBatch: BatchRequest = yield* typedSelect((state) =>
    selectDistributeGoalFromAnnotationsBatch(state, {
      goalsPositions: goals.map((goal, index) => ({
        goal: omit(goal, 'foundationallyEditable', 'partialDistribution'),
        // we remove foundationallyEditable and partialDistribution because they should not appear here.
        // and they confuse the selectDistributeGoalFromAnnotationsBatch since that's reused for all sorts of distributions.
        position: positions[index],
      })),
    }),
  );
  yield put(saveLlinkidApiBatch({ batch: resultBatch, applyInstantly: true }));
}

function* addCustomGoalSaga(action: AddCustomGoalPayloadAction) {
  const { parentItem, customItem } = action.payload;
  const schoolHref = yield* typedSelect(selectCurrentSchoolHref);
  const creatorHref = yield* typedSelect((state) => selectCreatorFromCustomCurricula(state)?.href);

  if (!creatorHref) {
    throw new Error('No creator found for custom curricula');
  }

  const customItemProperties = {
    ...customItem,
    type: CUSTOMTYPES.goal,
  };
  const customItemObject = createCustomItem(schoolHref, creatorHref, customItemProperties);

  const batch = generateCustomItemBatch(customItemObject);

  const goalListItems = yield* typedSelect(selectGoalsAndSectionAndGoalListFromCurriculum);

  const positions = getPositioningObject(
    parentItem,
    [customItemObject],
    CUSTOMCREATIONTYPES.llinkidGoal,
    goalListItems,
  );

  const resultBatch: BatchRequest = yield* typedSelect((state) =>
    selectDistributeGoalFromAnnotationsBatch(state, {
      goalsPositions: [{ goal: customItemObject, position: positions[0] }],
    }),
  );
  yield put(saveLlinkidApiBatch({ batch: [...resultBatch, ...batch], applyInstantly: true }));
}

function* addCustomSectionSaga(action: AddCustomSectionPayloadAction) {
  const { parent, type } = action.payload;
  const batch: BatchRequest = yield* typedSelect((state) =>
    selectAddCustomSectionBatch(state, {
      parentItem: parent,
      creationType: type,
    }),
  );
  yield put(saveLlinkidApiBatch({ batch, applyInstantly: true }));
}

export function* watchUserActionsSaga() {
  yield takeEvery(includeItem, includeItemSaga);
  yield takeEvery(moveItem, moveItemSaga);
  yield takeEvery(completeDistributionOrDeleteItem, completeDistributionOrDeleteItemSaga);
  yield takeEvery(changeItemTitle, changeTitleItemSaga);
  yield takeEvery(editCustomGoal, editCustomGoalSaga);
  yield takeEvery(distributeGoal, distributeGoalSaga);
  yield takeEvery(referenceGoal, referenceGoalSaga);
  yield takeEvery(addCustomGoal, addCustomGoalSaga);
  yield takeEvery(addCustomSection, addCustomSectionSaga);
}

function createIdleChannel() {
  return eventChannel((emitter) => {
    const events = [
      'load',
      'mousemove',
      'mousedown',
      'touchstart',
      'touchmove',
      'click',
      'keydown',
      'scroll',
    ];

    const userActive = () => {
      emitter('active');
    };

    events.forEach((event) => {
      window.addEventListener(event, userActive);
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, userActive);
      });
    };
  });
}

export function* watchUserIdleSaga() {
  const idleTimeout = settings.cacheTimeout.userIdleTime * 1000; // time is in milliseconds
  const idleChannel = yield call(createIdleChannel);

  let userIsIdle = false;

  while (true) {
    const { timeout } = yield race({
      timeout: delay(idleTimeout),
      activity: take(idleChannel),
    });

    if (timeout) {
      userIsIdle = true;
      yield put(setAutoSync(false));
      console.log('user is idle');
    } else if (userIsIdle) {
      userIsIdle = false;
      yield put(setAutoSync(true));
      console.log('user is no longer idle');
    }
  }
}
